import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { TOAST_TYPE } from '@base'
import BasicModal from '@base/basic/BasicModal'
import { Button, Input, Select } from '@baseV2'
import { CountryCodes, INSPECTION_USER_DESIGNATIONS, strings } from '@constants'
import { addOrderSiteManager, AddSiteManagerInEnquiry, getUserByID, getUsersOfOrganisation } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { getCountryDialCode, getNumberWithCountryCode, mobileLength } from '@utils'

import { CountryCodeWrapper, FieldErrorText, MobileFieldGroup, MobileRow, ModalActions, ModalBody, ModalTitle } from './styles'

const initialFormState = {
	full_name: {
		id: 'site-manager-full-name',
		label: 'Name',
		placeholder: 'Enter full name',
		value: '',
		dirty: false,
		required: true,
		valid: null,
	},
	mobile_number: {
		id: 'site-manager-mobile-number',
		label: 'Mobile',
		placeholder: 'Enter mobile number',
		value: '',
		dirty: false,
		required: true,
		valid: null,
	},
	email_id: {
		id: 'site-manager-email',
		label: 'Email',
		placeholder: 'Enter email',
		value: '',
		dirty: false,
		required: false,
	},
	designation: {
		id: 'site-manager-designation',
		label: 'Designation',
		placeholder: 'Enter designation',
		value: '',
		dirty: false,
		required: false,
	},
}

const deriveFormStateFromData = (oldState = initialFormState, data) => {
	const dialCode = getCountryDialCode(data?.country_code || defaultCountryCode)

	return {
		...oldState,
		full_name: { ...oldState.full_name, value: data?.full_name || '', dirty: false },
		mobile_number: {
			...oldState.mobile_number,
			value: data?.mobile_number?.replace(dialCode, '')?.trim() || '',
			dirty: false,
			valid: null,
		},
		email_id: { ...oldState.email_id, value: data?.email_id || data?.email || '', dirty: false },
		designation: { ...oldState.designation, value: data?.designation || '', dirty: false },
	}
}

const defaultCountryCode = CountryCodes.find((_c) => _c.default)
const designationTypes = Object.entries(INSPECTION_USER_DESIGNATIONS).map(([key, value]) => ({
	key,
	label: value,
}))
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const SiteManagerDetailsModal = ({
	isOpen,
	onClose,
	enquiry,
	order,
	update,
	onSaved,
	editable,
	organisationId = null,
	userId = null,
	viewOnly = false,
}) => {
	const dispatch = useDispatch()

	const [countryCode, setCountryCode] = useState(defaultCountryCode)
	const [formState, setFormState] = useState(initialFormState)
	const [userList, setUserList] = useState([])
	const [selectedUser, setSelectedUser] = useState(null)
	const user = useSelector((_state) => _state.getIn(['user', 'user']))

	useEffect(() => {
		if (!isOpen) return
		if (!userId) {
			resetHandler()
			return
		}
		getUserByID(userId)
			.then((response) => {
				const code = response?.mobile_number?.slice(0, 2)
				const result = CountryCodes.find((item) => item.dial_code.includes(code)) || getCountryDialCode(code)
				if (result) setCountryCode(result)
				setFormState((_s) => deriveFormStateFromData(_s, response))
			})
			.catch(console.log)
	}, [userId, isOpen])

	useEffect(() => {
		if (!isOpen || !organisationId) return

		dispatch(showLoader(true))
		getUsersOfOrganisation(organisationId)
			.then((response) => {
				setUserList(response || [])
			})
			.catch((error) => {
				const error_msg = error?.response?.data?.message ?? strings('msg_failed_to_fetch')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}, [organisationId, isOpen])

	const updateState = (key, updates) => {
		setFormState((_oldState) => ({
			..._oldState,
			[key]: {
				..._oldState[key],
				...updates,
			},
		}))
	}

	const handleUserSelect = (value) => {
		setFormState((_oldState) => deriveFormStateFromData(_oldState, value))
		setSelectedUser(value)
	}

	const handleDesignationChange = (value) => {
		updateState('designation', { value: value?.key ?? '' })
	}

	const isUserSelectDisabled = useMemo(() => {
		const { full_name, mobile_number, email_id, designation } = formState
		return !selectedUser && Boolean(full_name.value || mobile_number.value || email_id.value || designation.value)
	}, [formState.full_name.value, formState.mobile_number.value, formState.email_id.value, formState.designation.value])

	const areAllFieldsDisabled = !!selectedUser || viewOnly

	const submitHandler = () => {
		const { full_name, mobile_number, email_id, designation } = formState
		const mobileWithCode = getNumberWithCountryCode(countryCode, mobile_number.value)

		dispatch(showLoader(true))
		const API = order
			? addOrderSiteManager(full_name.value, mobileWithCode, email_id.value, designation.value, order?.order_id, user?.user_id, organisationId)
			: AddSiteManagerInEnquiry(
					full_name.value,
					mobileWithCode,
					email_id.value,
					designation.value,
					enquiry?.enquiry_id,
					user?.user_id,
					organisationId
				)

		API?.then((response) => {
			const toastMsg = editable ? strings(['update', 'site', 'manager', 'successfully']) : strings(['add', 'site', 'manager', 'successfully'])
			dispatch(showToast(true, toastMsg, { type: TOAST_TYPE.SUCCESS }))

			const siteManagerPatch = {
				user_id: selectedUser?.user_id ?? response?.user_id ?? response?.data?.user_id,
				full_name: full_name.value,
				mobile_number: mobileWithCode,
				email_id: email_id.value,
				designation: designation.value,
			}
			onSaved(
				order
					? { buyer_list: order.buyer_list.map((buyer, index) => (index === 0 ? { ...buyer, site_manager: siteManagerPatch } : buyer)) }
					: { site_manager: siteManagerPatch }
			)

			update()
			onClose()
		})
			.catch((error) => {
				const error_msg = error?.response?.data?.message || error?.data?.message || strings('msg_something_went_wrong')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const resetHandler = () => {
		setFormState(initialFormState)
		setSelectedUser(null)
		setCountryCode(defaultCountryCode)
	}

	const formErrors = useMemo(() => {
		const errors = {}
		if (!formState.full_name.value?.trim()) {
			errors.full_name = strings('name_required')
		}
		if (!formState.mobile_number.value) {
			errors.mobile_number = strings('mob_num_required')
		} else if (String(formState.mobile_number.value).length !== mobileLength(countryCode.dial_code)) {
			errors.mobile_number = strings('enter_valid_num')
		}
		if (formState.email_id.value && !EMAIL_REGEX.test(formState.email_id.value)) {
			errors.email_id = strings('enter_valid_email')
		}
		return errors
	}, [formState.full_name.value, formState.mobile_number.value, formState.email_id.value, countryCode.dial_code])

	const disabledHandler = useMemo(() => {
		if (viewOnly) return true
		if (selectedUser) return false
		return Object.keys(formErrors).length > 0
	}, [formErrors, selectedUser, viewOnly])

	const title = editable ? strings(['update', 'site', 'manager']) : strings(['add', 'site', 'manager'])

	return (
		<BasicModal isOpen={isOpen} onClose={onClose} width='40vw' closeIcon={true}>
			<ModalTitle>{title}</ModalTitle>
			<ModalBody>
				<Select
					id='site-manager-select-user'
					label={strings('user')}
					placeholder={strings('select', 'user')}
					data={userList ?? []}
					value={userList?.find((item) => item.user_id === selectedUser?.user_id) ?? {}}
					primaryKey='user_id'
					displayKey='full_name'
					onChange={handleUserSelect}
					disabled={isUserSelectDisabled || viewOnly}
				/>

				<Input
					id={formState.full_name.id}
					label={formState.full_name.label}
					placeholder={formState.full_name.placeholder}
					value={formState.full_name.value ?? ''}
					required={formState.full_name.required}
					error={formState.full_name.dirty ? formErrors.full_name : undefined}
					onChange={(e) => updateState('full_name', { value: e.target.value, dirty: true })}
					disabled={areAllFieldsDisabled}
				/>

				<MobileFieldGroup>
					<MobileRow>
						<CountryCodeWrapper>
							<Select
								id='site-manager-country-code'
								data={CountryCodes}
								displayKey='dial_code'
								primaryKey='name'
								type='tel'
								noDropIcon
								value={countryCode}
								onChange={setCountryCode}
								disabled={areAllFieldsDisabled}
							/>
						</CountryCodeWrapper>
						<Input
							id={formState.mobile_number.id}
							type='tel'
							label={formState.mobile_number.label}
							placeholder={formState.mobile_number.placeholder}
							value={formState.mobile_number.value ?? ''}
							maxLength={mobileLength(countryCode.dial_code)}
							required={formState.mobile_number.required}
							onChange={(e) => updateState('mobile_number', { value: e.target.value, dirty: true })}
							disabled={areAllFieldsDisabled}
						/>
					</MobileRow>
					{formState.mobile_number.dirty && formErrors.mobile_number && <FieldErrorText>{formErrors.mobile_number}</FieldErrorText>}
				</MobileFieldGroup>

				<Input
					id={formState.email_id.id}
					label={formState.email_id.label}
					type='email'
					placeholder={formState.email_id.placeholder}
					value={formState.email_id.value}
					required={formState.email_id.required}
					error={formState.email_id.dirty ? formErrors.email_id : undefined}
					onChange={(e) => updateState('email_id', { value: e.target.value, dirty: true })}
					disabled={areAllFieldsDisabled || selectedUser?.email_id}
				/>

				<Select
					id='site-manager-select-designation'
					label={strings('designation')}
					placeholder={strings('select', 'designation')}
					data={designationTypes ?? []}
					value={designationTypes.find((item) => item.key === formState.designation.value) ?? {}}
					disabled={areAllFieldsDisabled}
					primaryKey='key'
					displayKey='label'
					required={formState.designation.required}
					onChange={handleDesignationChange}
				/>
			</ModalBody>

			<ModalActions>
				<Button variant='ghost' color='danger' size='small' onClick={resetHandler} disabled={viewOnly}>
					{strings('reset')}
				</Button>
				<Button size='small' disabled={disabledHandler} onClick={submitHandler}>
					{editable ? strings('update') : strings('add')}
				</Button>
			</ModalActions>
		</BasicModal>
	)
}

export default SiteManagerDetailsModal
